import {
  CDBalancePaymentMethod,
  CDBalancePaymentTime,
  CDBalanceTermsBeginOn,
  LoadboardMethod,
  LoadboardTerms,
} from '#/shared/api/generated/loadboard/models'
import type {
  ShipmentUpdateDispatchCodMethod,
  ShipmentUpdateDispatchPaymentTerm,
  ShipmentUpdateDispatchTermBegins,
  ShipmentUpdatePaymentMethod,
  ShipmentUpdatePaymentTerms,
} from '#/shared/api/generated/shipment/models'

/**
 * The shipment payment forms speak the Central-Dispatch / loadboard vocabulary
 * (the option builders in `entities/shipment/lib/payment-options.ts`), but the
 * shipment service now validates a narrower canonical set on write. These
 * tables translate between the two.
 *
 * Selections with no canonical equivalent (e.g. Comchek, TCH, certified funds,
 * money order, net-days terms) map to `null` on write — they cannot be stored
 * on the shipment and are dropped. On read we reverse-map canonical values back
 * to a representative form option so a saved selection re-displays; values that
 * are already loadboard-shaped (legacy rows) pass through unchanged.
 */

// --- write: form (loadboard / CD) -> shipment canonical ---

const PAYMENT_METHOD_TO_SHIPMENT: Partial<
  Record<string, ShipmentUpdatePaymentMethod>
> = {
  [LoadboardMethod.cash]: 'cash',
  [LoadboardMethod.check]: 'check',
  [LoadboardMethod.cashiers_check]: 'check',
  [LoadboardMethod.ach]: 'ach',
  [LoadboardMethod.credit_card]: 'card',
  [LoadboardMethod.zelle]: 'zelle',
}

// Cash-vs-check is carried by payment_method; terms only encode *when*.
const PAYMENT_TERMS_TO_SHIPMENT: Partial<
  Record<string, ShipmentUpdatePaymentTerms>
> = {
  [LoadboardTerms.cash_on_delivery]: 'cash_on_delivery',
  [LoadboardTerms.check_on_delivery]: 'cash_on_delivery',
  [LoadboardTerms.cash_on_pickup]: 'cash_on_pickup',
  [LoadboardTerms.check_on_pickup]: 'cash_on_pickup',
}

const COD_METHOD_TO_SHIPMENT: Partial<
  Record<string, ShipmentUpdateDispatchCodMethod>
> = {
  [CDBalancePaymentMethod.CASH]: 'cash',
  [CDBalancePaymentMethod.COMPANY_CHECK]: 'check',
}

const PAYMENT_TERM_TO_SHIPMENT: Partial<
  Record<string, ShipmentUpdateDispatchPaymentTerm>
> = {
  [CDBalancePaymentTime.IMMEDIATELY]: 'immediately',
  [CDBalancePaymentTime.TWO_BUSINESS_DAYS_QUICK_PAY]: '2days',
  [CDBalancePaymentTime.FIVE_BUSINESS_DAYS]: '5days',
  [CDBalancePaymentTime.TEN_BUSINESS_DAYS]: '10days',
  [CDBalancePaymentTime.FIFTEEN_BUSINESS_DAYS]: '15days',
  [CDBalancePaymentTime.THIRTY_BUSINESS_DAYS]: '30days',
}

const TERM_BEGINS_TO_SHIPMENT: Partial<
  Record<string, ShipmentUpdateDispatchTermBegins>
> = {
  [CDBalanceTermsBeginOn.PICKUP]: 'pickup',
  [CDBalanceTermsBeginOn.DELIVERY]: 'delivery',
  [CDBalanceTermsBeginOn.RECEIVING_SIGNED_BOL]: 'signed',
}

export function toShipmentPaymentMethod(
  value: string,
): ShipmentUpdatePaymentMethod {
  return PAYMENT_METHOD_TO_SHIPMENT[value] ?? null
}

export function toShipmentPaymentTerms(
  value: string,
): ShipmentUpdatePaymentTerms {
  return PAYMENT_TERMS_TO_SHIPMENT[value] ?? null
}

export function toShipmentCodMethod(
  value: string,
): ShipmentUpdateDispatchCodMethod {
  return COD_METHOD_TO_SHIPMENT[value] ?? null
}

export function toShipmentPaymentTerm(
  value: string,
): ShipmentUpdateDispatchPaymentTerm {
  return PAYMENT_TERM_TO_SHIPMENT[value] ?? null
}

export function toShipmentTermBegins(
  value: string,
): ShipmentUpdateDispatchTermBegins {
  return TERM_BEGINS_TO_SHIPMENT[value] ?? null
}

// --- read: shipment canonical -> form (loadboard / CD) ---

const PAYMENT_METHOD_TO_FORM: Partial<Record<string, string>> = {
  card: LoadboardMethod.credit_card,
  check: LoadboardMethod.check,
  cash: LoadboardMethod.cash,
  ach: LoadboardMethod.ach,
  zelle: LoadboardMethod.zelle,
}

// Canonical values are loadboard-shaped now, so they pass through `toForm`
// unchanged; only the retired `cod` value needs a mapping for rows saved
// before the vocabulary change.
const PAYMENT_TERMS_TO_FORM: Partial<Record<string, string>> = {
  cod: LoadboardTerms.cash_on_delivery,
}

const COD_METHOD_TO_FORM: Partial<Record<string, string>> = {
  cash: CDBalancePaymentMethod.CASH,
  check: CDBalancePaymentMethod.COMPANY_CHECK,
}

const PAYMENT_TERM_TO_FORM: Partial<Record<string, string>> = {
  immediately: CDBalancePaymentTime.IMMEDIATELY,
  '2days': CDBalancePaymentTime.TWO_BUSINESS_DAYS_QUICK_PAY,
  '5days': CDBalancePaymentTime.FIVE_BUSINESS_DAYS,
  '10days': CDBalancePaymentTime.TEN_BUSINESS_DAYS,
  '15days': CDBalancePaymentTime.FIFTEEN_BUSINESS_DAYS,
  '30days': CDBalancePaymentTime.THIRTY_BUSINESS_DAYS,
}

const TERM_BEGINS_TO_FORM: Partial<Record<string, string>> = {
  pickup: CDBalanceTermsBeginOn.PICKUP,
  delivery: CDBalanceTermsBeginOn.DELIVERY,
  signed: CDBalanceTermsBeginOn.RECEIVING_SIGNED_BOL,
}

/**
 * Map a stored shipment value back to the form's option vocabulary. Canonical
 * values reverse-map to a representative option; legacy loadboard-shaped values
 * (not a canonical key) pass through so they still match an option; empty/unset
 * becomes `''`.
 */
function toForm(
  table: Partial<Record<string, string>>,
  value: string | null | undefined,
): string {
  if (!value) return ''
  return table[value] ?? value
}

export const toFormPaymentMethod = (value: string | null | undefined): string =>
  toForm(PAYMENT_METHOD_TO_FORM, value)

export const toFormPaymentTerms = (value: string | null | undefined): string =>
  toForm(PAYMENT_TERMS_TO_FORM, value)

export const toFormCodMethod = (value: string | null | undefined): string =>
  toForm(COD_METHOD_TO_FORM, value)

export const toFormPaymentTerm = (value: string | null | undefined): string =>
  toForm(PAYMENT_TERM_TO_FORM, value)

export const toFormTermBegins = (value: string | null | undefined): string =>
  toForm(TERM_BEGINS_TO_FORM, value)
